import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useHistory } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import TextField from '@material-ui/core/TextField'
import TipWell from 'components/tip-well'
import { PhoneMaskedField } from 'components/form-util/phone'
import { amplitudeEvent } from 'lib/amplitude'
import { redirectToLogin } from 'lib/auth'
import useFieldStyles from 'pages/things-to-do/events/register/registration-with-payment-intents/redesign/styles'
import {
  digitsOnly,
  loadGuestBooking,
  saveGuestBooking,
  withGuestBookingParam,
} from '../../guest-booking'
import EmailField from './email-field'
import { FormShell } from './form-parts'
import SegmentedControl from './segmented-control'
import useStyles from './styles'

export const BOOKING_FOR_OPTIONS = [
  { value: 'me', label: 'Me' },
  { value: 'someone_else', label: 'Someone else' },
]

export const AGE_GROUP_OPTIONS = [
  { value: 'adult', label: 'Adult' },
  { value: 'junior', label: 'Junior' },
]

// A second contact (the booker) is needed whenever the booker is not the
// student: a junior's parent or guardian, or someone booking for another adult.
const needsSeparateContact = (attendeeType, bookingFor) =>
  attendeeType === 'junior' || bookingFor === 'someone_else'

const requiredWhenSeparateContact = message => (
  attendeeType,
  bookingFor,
  schema,
) =>
  needsSeparateContact(attendeeType, bookingFor)
    ? schema.trim().required(message)
    : schema

// Same rules as the legacy guest form; the booker's own name fields are the
// legacy guardian name fields.
export const schema = yup.object().shape({
  bookingFor: yup
    .string()
    .oneOf(['me', 'someone_else'])
    .required(),
  studentFirstName: yup
    .string()
    .trim()
    .required('First name is required'),
  studentLastName: yup
    .string()
    .trim()
    .required('Last name is required'),
  attendeeType: yup
    .string()
    .oneOf(['adult', 'junior'])
    .required(),
  contactFirstName: yup
    .string()
    .when(
      ['attendeeType', 'bookingFor'],
      requiredWhenSeparateContact('First name is required'),
    ),
  contactLastName: yup
    .string()
    .when(
      ['attendeeType', 'bookingFor'],
      requiredWhenSeparateContact('Last name is required'),
    ),
  email: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Email is required'),
  phone: yup
    .string()
    .required('Phone number is required')
    .test(
      'ten-digits',
      'Enter a 10-digit phone number',
      value => digitsOnly(value).length === 10,
    ),
})

// Restores what the golfer typed if they came back from the payment step.
export const storedDefaults = () => {
  const stored = loadGuestBooking()
  if (!stored) {
    return {
      bookingFor: 'me',
      attendeeType: 'adult',
      studentFirstName: '',
      studentLastName: '',
      contactFirstName: '',
      contactLastName: '',
      email: '',
      phone: '',
    }
  }
  const { student, contact } = stored
  const isJunior = !!student.isMinor
  const sameName =
    contact.firstName === student.firstName &&
    contact.lastName === student.lastName
  return {
    bookingFor: !isJunior && sameName ? 'me' : 'someone_else',
    attendeeType: isJunior ? 'junior' : 'adult',
    studentFirstName: student.firstName,
    studentLastName: student.lastName,
    contactFirstName: sameName && !isJunior ? '' : contact.firstName,
    contactLastName: sameName && !isJunior ? '' : contact.lastName,
    email: contact.email,
    phone: contact.phone,
  }
}

// Browser autofill reasoning: autofill fills the person typing. The booker's
// own fields (name, email, phone, and the "Your details" / "Parent or guardian"
// names) get the real tokens. The student's names only do so when the student
// is the person typing (Me + Adult); for Someone else or a junior the student
// is somebody else, so those two fields opt out and the browser does not paste
// the booker's name into another person's record.
const NAME_INPUT_PROPS = {
  'aria-required': true,
  autoCapitalize: 'words',
  spellCheck: false,
}

const GuestDetailsForm = ({
  currentUrl,
  submitLabel,
  priceLabel,
  belowCard,
}) => {
  const classes = useStyles()
  const fieldClasses = useFieldStyles()
  const history = useHistory()
  const { register, handleSubmit, errors, control, watch } = useForm({
    defaultValues: storedDefaults(),
    resolver: yupResolver(schema),
  })
  const attendeeType = watch('attendeeType')
  const bookingFor = watch('bookingFor')
  const isJunior = attendeeType === 'junior'
  const isSomeoneElse = bookingFor === 'someone_else'
  const hasSeparateContact = needsSeparateContact(attendeeType, bookingFor)
  const studentIsTyping = !hasSeparateContact

  useEffect(() => {
    amplitudeEvent('view-guest-details-form', { is_guest: true })
  }, [])

  const onSubmit = values => {
    const student = {
      firstName: values.studentFirstName.trim(),
      lastName: values.studentLastName.trim(),
      isMinor: isJunior,
    }
    const contact = {
      firstName: hasSeparateContact
        ? values.contactFirstName.trim()
        : student.firstName,
      lastName: hasSeparateContact
        ? values.contactLastName.trim()
        : student.lastName,
      email: values.email.trim(),
      phone: digitsOnly(values.phone),
    }
    amplitudeEvent('submit-guest-details', {
      is_guest: true,
      attendee_type: values.attendeeType,
    })
    saveGuestBooking({ contact, student })
    history.push(withGuestBookingParam(currentUrl))
  }

  const loginTip = (
    <TipWell
      variant="neutral"
      caption={
        <span className={classes.signInTipBody}>
          Have an account?{' '}
          <button
            type="button"
            className={classes.signInLink}
            onClick={() => redirectToLogin('consumer')}
          >
            Log in
          </button>{' '}
          to fill this in automatically.
        </span>
      }
    />
  )

  return (
    <FormShell
      onSubmit={handleSubmit(onSubmit)}
      tip={loginTip}
      submitLabel={submitLabel}
      priceLabel={priceLabel}
      belowCard={belowCard}
    >
      <Controller
        name="bookingFor"
        control={control}
        render={({ value, onChange }) => (
          <SegmentedControl
            name="bookingFor"
            label="Who is taking the lesson"
            options={BOOKING_FOR_OPTIONS}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <div
        className={clsx(fieldClasses.fieldGrid, classes.fields)}
        role={hasSeparateContact ? 'group' : undefined}
        aria-label={hasSeparateContact ? 'Student' : undefined}
      >
        <TextField
          id="guest-student-first-name"
          name="studentFirstName"
          inputRef={register}
          label={isSomeoneElse ? 'Student first name' : 'First name'}
          autoComplete={studentIsTyping ? 'given-name' : 'off'}
          error={!!errors.studentFirstName}
          helperText={errors.studentFirstName?.message}
          inputProps={NAME_INPUT_PROPS}
        />
        <TextField
          id="guest-student-last-name"
          name="studentLastName"
          inputRef={register}
          label={isSomeoneElse ? 'Student last name' : 'Last name'}
          autoComplete={studentIsTyping ? 'family-name' : 'off'}
          error={!!errors.studentLastName}
          helperText={errors.studentLastName?.message}
          inputProps={NAME_INPUT_PROPS}
        />
      </div>

      <div className={classes.ageRow}>
        <p className={classes.ageLabel} id="guest-age-group-label">
          Age group
        </p>
        <Controller
          name="attendeeType"
          control={control}
          render={({ value, onChange }) => (
            <SegmentedControl
              name="attendeeType"
              labelledBy="guest-age-group-label"
              options={AGE_GROUP_OPTIONS}
              value={value}
              onChange={onChange}
            />
          )}
        />
      </div>

      <div
        role={hasSeparateContact ? 'group' : undefined}
        aria-labelledby={
          hasSeparateContact ? 'guest-contact-heading' : undefined
        }
      >
        {hasSeparateContact && (
          <>
            <h2 className={classes.subheading} id="guest-contact-heading">
              {isJunior ? 'Parent or guardian' : 'Your details'}
            </h2>
            <div className={clsx(fieldClasses.fieldGrid, classes.fields)}>
              <TextField
                id="guest-contact-first-name"
                name="contactFirstName"
                inputRef={register}
                label="First name"
                autoComplete="given-name"
                error={!!errors.contactFirstName}
                helperText={errors.contactFirstName?.message}
                inputProps={NAME_INPUT_PROPS}
              />
              <TextField
                id="guest-contact-last-name"
                name="contactLastName"
                inputRef={register}
                label="Last name"
                autoComplete="family-name"
                error={!!errors.contactLastName}
                helperText={errors.contactLastName?.message}
                inputProps={NAME_INPUT_PROPS}
              />
            </div>
          </>
        )}

        <div className={clsx(fieldClasses.fieldGrid, classes.fields)}>
          <EmailField
            id="guest-email"
            label="Email"
            control={control}
            name="email"
            errors={errors}
          />
          <TextField
            id="guest-phone"
            name="phone"
            inputRef={register}
            label="Phone number"
            autoComplete="tel"
            InputProps={{ inputComponent: PhoneMaskedField }}
            inputProps={{ 'aria-required': true, inputMode: 'tel' }}
            error={!!errors.phone}
            helperText={errors.phone?.message}
          />
        </div>
      </div>
    </FormShell>
  )
}

GuestDetailsForm.propTypes = {
  currentUrl: PropTypes.string.isRequired,
  submitLabel: PropTypes.string.isRequired,
  priceLabel: PropTypes.string,
  belowCard: PropTypes.node,
}

export default GuestDetailsForm
